/* ==========================================================================
   PROMO SUITE
   Countdown, click-to-copy discount codes, and the promo popup.
   Self-contained: no theme.js internals are relied on, so a theme update
   will not break any of this.
   ========================================================================== */
(function () {
  'use strict';

  /* ------------------------------------------------------------------------
     <promo-countdown data-ends="1785801540">
     data-ends is a UNIX timestamp in SECONDS, already resolved to real UTC
     by promo.liquid, so the browser's own timezone never enters into it.
     ------------------------------------------------------------------------ */
  class PromoCountdown extends HTMLElement {
    connectedCallback() {
      this.output = this.querySelector('[data-promo-countdown-value]');
      this.endsAt = parseInt(this.dataset.ends, 10) * 1000;

      if (!this.output || !this.endsAt || isNaN(this.endsAt)) {
        this.hidden = true;
        return;
      }

      this.tick();
      this.timer = setInterval(this.tick.bind(this), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const remaining = this.endsAt - Date.now();

      if (remaining <= 0) {
        clearInterval(this.timer);
        this.expire();
        return;
      }

      const totalSeconds = Math.floor(remaining / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      // Over a day out, seconds are noise. Under a day, they create urgency.
      this.output.textContent =
        days > 0
          ? days + 'd ' + pad(hours) + 'h ' + pad(minutes) + 'm'
          : pad(hours) + ':' + pad(minutes) + ':' + pad(seconds);
    }

    // The promotion has run out while the page was open. Take the whole
    // callout down rather than leaving a dead offer on screen — this also
    // covers pages served from Shopify's cache a little after the end time.
    expire() {
      const root = this.closest('.promo');
      if (root) {
        root.setAttribute('data-promo-expired', '');
      } else {
        this.hidden = true;
      }
      document.dispatchEvent(new CustomEvent('promo:expired'));
    }
  }

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  if (!customElements.get('promo-countdown')) {
    customElements.define('promo-countdown', PromoCountdown);
  }

  /* ------------------------------------------------------------------------
     Click-to-copy discount codes.
     Delegated, so codes rendered later (ajax cart, quick view) work too.
     ------------------------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    const button = event.target.closest('button.promo__code');
    if (!button) return;

    event.preventDefault();
    const code = button.dataset.promoCode;
    if (!code) return;

    copyText(code).then(function (ok) {
      if (!ok) return;
      button.setAttribute('data-copied', '');
      clearTimeout(button._promoResetTimer);
      button._promoResetTimer = setTimeout(function () {
        button.removeAttribute('data-copied');
      }, 2000);
    });
  });

  function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).then(
        function () { return true; },
        function () { return legacyCopy(text); }
      );
    }
    return Promise.resolve(legacyCopy(text));
  }

  function legacyCopy(text) {
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.cssText = 'position:fixed;top:-9999px;opacity:0;';
    document.body.appendChild(field);
    field.select();

    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch (e) {
      ok = false;
    }

    document.body.removeChild(field);
    return ok;
  }

  /* ------------------------------------------------------------------------
     Promo popup
     ------------------------------------------------------------------------ */
  function initPopup(root) {
    if (!root || root._promoInit) return;
    root._promoInit = true;

    const dialog = root.querySelector('.promo-popup__dialog');
    const testMode = root.dataset.testMode === 'true';
    const delay = parseInt(root.dataset.delaySeconds, 10) || 0;
    const frequencyDays = parseInt(root.dataset.frequencyDays, 10);
    // Key the frequency cap to the campaign itself, so dismissing one
    // promotion does not silence the next one.
    const handleEl = root.querySelector('[data-promo-handle]');
    const campaign = (handleEl && handleEl.dataset.promoHandle) || 'default';
    const storageKey = 'promo_popup_' + campaign;
    const triggerOnExit = root.dataset.exitIntent === 'true';

    let lastFocused = null;
    let opened = false;

    // In the theme editor the popup should always be reachable.
    if (!testMode && isSuppressed()) return;

    function isSuppressed() {
      try {
        const until = parseInt(window.localStorage.getItem(storageKey), 10);
        return !!until && Date.now() < until;
      } catch (e) {
        return false; // private browsing etc — just show it
      }
    }

    function suppress() {
      if (testMode) return;
      try {
        const days = isNaN(frequencyDays) ? 7 : frequencyDays;
        window.localStorage.setItem(storageKey, Date.now() + days * 86400000);
      } catch (e) {
        /* storage unavailable — the popup simply shows again next visit */
      }
    }

    function open() {
      if (opened) return;
      opened = true;
      lastFocused = document.activeElement;
      root.setAttribute('data-open', '');
      root.removeAttribute('aria-hidden');
      if (dialog) dialog.focus();
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      opened = false;
      root.removeAttribute('data-open');
      root.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', onKeydown);
      suppress();
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    function onKeydown(event) {
      if (event.key === 'Escape') close();
    }

    root.querySelectorAll('[data-promo-popup-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });

    // Following the CTA counts as engagement — don't nag on the next visit.
    const cta = root.querySelector('.promo__popup-cta');
    if (cta) cta.addEventListener('click', suppress);

    // Copying the code is the whole point of the popup; close once they have it.
    root.addEventListener('click', function (event) {
      if (event.target.closest('button.promo__code')) {
        setTimeout(close, 1200);
      }
    });

    // An expired promo should take the popup with it.
    document.addEventListener('promo:expired', function () {
      if (opened) close();
      root.remove();
    });

    if (triggerOnExit) {
      document.addEventListener('mouseout', function onExit(event) {
        if (event.clientY <= 0 && !event.relatedTarget) {
          document.removeEventListener('mouseout', onExit);
          open();
        }
      });
    }

    setTimeout(open, delay * 1000);
  }

  function initAll() {
    document.querySelectorAll('.promo-popup').forEach(initPopup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    const popup = event.target.querySelector('.promo-popup');
    if (popup) initPopup(popup);
  });

  /* ------------------------------------------------------------------------
     Free gift  (snippets/free-gift.liquid)
     - "Add my free gift" adds the gift as its own cart line, tagged with the
       _free_gift property so it is never confused with a bought one. The
       automatic Buy X get Y discount then takes it to $0 at checkout.
     - The cart snippet flags any gift line that should not be there (spend
       dropped under the threshold, gift ended or sold out, duplicates) with
       [data-free-gift-fix]. Those fixes are applied on load, after every
       cart rebuild, and before checkout, otherwise the customer would pay
       full price for a "free" gift.
     ------------------------------------------------------------------------ */
  const giftFixAttempts = {};
  let giftFixing = false;

  function cartRoute(name, fallback) {
    return (window.theme && window.theme.routes && window.theme.routes[name]) || fallback;
  }

  function cartPost(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) throw data;
        return data;
      });
    });
  }

  function changeGiftLine(key, quantity) {
    return cartPost(cartRoute('cartChange', '/cart/change.js'), { id: key, quantity: quantity });
  }

  // The theme's CartForm re-renders the line items and totals on cart:build.
  // Anywhere without a cart form there is nothing to re-render, so reload.
  function rebuildCart() {
    if (document.querySelector('form[data-location] [data-products]')) {
      document.dispatchEvent(new CustomEvent('cart:build'));
    } else {
      window.location.reload();
    }
  }

  // Two tries per line, so a fix that keeps failing can never loop or lock
  // the customer out of checkout.
  function pendingGiftFixes() {
    return Array.prototype.filter.call(
      document.querySelectorAll('[data-free-gift-fix]'),
      function (el) { return (giftFixAttempts[el.dataset.key] || 0) < 2; }
    );
  }

  function applyGiftFixes() {
    if (giftFixing) return;
    const fixes = pendingGiftFixes();
    if (!fixes.length) return;

    giftFixing = true;
    fixes
      .reduce(function (chain, el) {
        const key = el.dataset.key;
        giftFixAttempts[key] = (giftFixAttempts[key] || 0) + 1;
        return chain.then(function () {
          return changeGiftLine(key, parseInt(el.dataset.qty, 10) || 0);
        });
      }, Promise.resolve())
      .catch(function () { /* the next render re-flags it; the attempt cap stops retries */ })
      .then(function () {
        giftFixing = false;
        rebuildCart();
      });
  }

  document.addEventListener('click', function (event) {
    const addButton = event.target.closest('[data-free-gift-add]');
    if (addButton) {
      event.preventDefault();
      if (addButton.disabled) return;
      addButton.disabled = true;
      addButton.classList.add('is-loading');

      cartPost(cartRoute('cartAdd', '/cart/add.js'), {
        items: [{
          id: parseInt(addButton.dataset.variantId, 10),
          quantity: 1,
          properties: { _free_gift: addButton.dataset.handle || 'free-gift' }
        }]
      })
        .then(rebuildCart)
        .catch(function (error) {
          addButton.classList.remove('is-loading');
          addButton.textContent = (error && error.description) || 'Sorry, the gift is unavailable';
        });
      return;
    }

    const removeButton = event.target.closest('[data-free-gift-remove]');
    if (removeButton) {
      event.preventDefault();
      if (removeButton.disabled) return;
      removeButton.disabled = true;
      changeGiftLine(removeButton.dataset.key, 0).then(rebuildCart, rebuildCart);
    }
  });

  // Last line of defence: never let the cart post to checkout while a gift
  // line is flagged. Capture phase, so it runs before the theme's handler.
  document.addEventListener('submit', function (event) {
    if (!event.target.matches('form[data-location]')) return;
    if (!pendingGiftFixes().length) return;
    event.preventDefault();
    event.stopPropagation();
    applyGiftFixes();
  }, true);

  function watchGiftFixes() {
    document.querySelectorAll('[data-products]').forEach(function (list) {
      if (list._freeGiftWatch) return;
      list._freeGiftWatch = true;
      new MutationObserver(applyGiftFixes).observe(list, { childList: true });
    });
    applyGiftFixes();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchGiftFixes);
  } else {
    watchGiftFixes();
  }
})();
